import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App } from 'antd';

import useSettledValue, { SELECTION_SETTLE_MS } from './useSettledValue';
import { confirmItem, getItemConfirmations, withdrawConfirmation } from '../services/confirmations';
import { confirmationRefusal, confirmationsLabel, isConfirmationsElement, readCount } from '../utils/confirmationValues';
import { normalizeId } from '../utils/ids';

/**
 * "ME TOO" for one item's read view (KPR-390; services/confirmations.js has
 * the contract). One GET per item — not per element — and only when its card
 * has a confirmations element, once the selection has rested (a list's ↑/↓
 * walks items at key-repeat speed; SELECTION_SETTLE_MS is the read view's own
 * wait for its sections).
 *
 * → `{ stateFor(name), toggle(name), status }`, where `stateFor` is
 * `{ count, confirmed, canConfirm, canSeeWho, busy }` for an element this
 * item's answer named, or null: before the answer lands, for a card without
 * the element, and when the GET failed (a 404, or a network error — an API
 * without the door yet). A surface given null shows the count alone, from the
 * item, and no button.
 *
 * `toggle` is optimistic: the press flips the state and moves the count at
 * once, then settles on the server's `{ count, confirmed }`, and pushes the
 * settled count into the item IN PLACE through `onItemUpdated` (the pattern of
 * hooks/useItemOwnTag.js) — a confirmation moves no `updatedAt`, so it is
 * never an edit. A refusal puts both back and says why
 * (utils/confirmationValues.js confirmationRefusal); `confirm_not_allowed`
 * asks again, so the button goes when the person may no longer press it.
 */
export default function useItemConfirmations({ item, elementDefs, enabled = true, onItemUpdated = null }) {
	const { message } = App.useApp();
	const itemId = normalizeId(item?._id);
	const saved = Boolean(itemId) && itemId !== '-1';
	const labels = useMemo(() => {
		const out = {};
		(elementDefs || []).filter(isConfirmationsElement).forEach((def) => {
			if (def.name) out[def.name] = confirmationsLabel(def);
		});
		return out;
	}, [elementDefs]);
	const hasElement = Object.keys(labels).length > 0;
	const settledId = useSettledValue(itemId, SELECTION_SETTLE_MS);
	const ask = enabled && saved && hasElement && settledId === itemId;

	// Keyed by the item it describes, so an answer never shows on the next one.
	const [answer, setAnswer] = useState({ itemId: null, status: 'idle', byElement: {} });
	const [reloadKey, setReloadKey] = useState(0);

	useEffect(() => {
		if (!ask) return undefined;
		let cancelled = false;
		setAnswer(prev => (prev.itemId === itemId ? prev : { itemId, status: 'loading', byElement: {} }));
		getItemConfirmations(itemId)
			.then((data) => {
				if (cancelled) return;
				const byElement = {};
				data.elements.forEach((entry) => {
					if (!entry?.element) return;
					byElement[entry.element] = {
						count: readCount(entry.count),
						confirmed: entry.confirmed === true,
						canConfirm: entry.canConfirm === true,
						canSeeWho: entry.canSeeWho === true,
						busy: false,
					};
				});
				setAnswer({ itemId, status: 'ready', byElement });
			})
			.catch(() => {
				// A 404 (the item went, or an API without the door) or no answer at
				// all: the count stays readable from the item; the button goes.
				if (!cancelled) setAnswer({ itemId, status: 'unavailable', byElement: {} });
			});
		return () => { cancelled = true; };
	}, [ask, itemId, reloadKey]);

	const current = useMemo(
		() => (answer.itemId === itemId ? answer : { itemId, status: 'idle', byElement: {} }),
		[answer, itemId]
	);

	// The latest item and answer, for a press that settles after a re-render.
	const itemRef = useRef(item);
	itemRef.current = item;
	const answerRef = useRef(current);
	answerRef.current = current;

	const patch = useCallback((forItem, name, change) => {
		setAnswer((prev) => {
			if (prev.itemId !== forItem || !prev.byElement[name]) return prev;
			return { ...prev, byElement: { ...prev.byElement, [name]: { ...prev.byElement[name], ...change } } };
		});
	}, []);

	const toggle = useCallback(async (name) => {
		const forItem = itemId;
		const before = answerRef.current.byElement[name];
		if (!before || before.busy) return;
		const confirming = !before.confirmed;
		// Withdrawing is always allowed; confirming only where the API said so.
		if (confirming && !before.canConfirm) return;
		patch(forItem, name, {
			confirmed: confirming,
			count: Math.max(0, before.count + (confirming ? 1 : -1)),
			busy: true,
		});
		try {
			const data = confirming ? await confirmItem(forItem, name) : await withdrawConfirmation(forItem, name);
			const count = readCount(data?.count);
			patch(forItem, name, { count, confirmed: data?.confirmed === true, busy: false });
			const latest = itemRef.current;
			if (typeof onItemUpdated === 'function' && latest && normalizeId(latest._id) === forItem) {
				onItemUpdated({ ...latest, elements: { ...(latest.elements || {}), [name]: count } });
			}
		} catch (error) {
			patch(forItem, name, { count: before.count, confirmed: before.confirmed, busy: false });
			message.error(confirmationRefusal(error, { label: labels[name] }));
			// The collection stopped letting this person confirm: ask again, so
			// the button goes rather than refusing every press.
			if (error?.response?.data?.code === 'confirm_not_allowed') setReloadKey(n => n + 1);
		}
	}, [itemId, patch, onItemUpdated, message, labels]);

	const stateFor = useCallback(
		name => (current.status === 'ready' ? current.byElement[name] || null : null),
		[current]
	);

	return { stateFor, toggle, status: current.status };
}
